<template>
    <modal
        name="modalProgramGroupContributionHistory"
        :width="'78%'"
        :height="'auto'"
        :click-to-close="false"
    >
        <!-- Cerrar -->
        <button
            type="button"
            class="btn btn-danger btn-sm btn-closed"
            @click="closeModal"
        >
            <i class="fa-solid fa-xmark"></i>
        </button>

        <!-- Header -->
        <div class="modal-header contribution-modal-header">
            <div>
                <h5 class="modal-title">
                    Historial de aportaciones
                </h5>

                <div class="investor-name">
                    <i class="fa-solid fa-user mr-1"></i>
                    {{ user_name }}
                </div>
            </div>
        </div>

        <div class="modal-body contribution-modal-body">

            <!-- Loading -->
            <div v-if="loading" class="loading-container">
                <i class="fa-solid fa-circle-notch fa-spin fa-3x"></i>

                <p class="mb-0 mt-3">
                    Cargando historial de aportaciones...
                </p>
            </div>

            <template v-else>

                <!-- Resumen -->
                <div
                    v-if="history.length > 0"
                    class="history-summary"
                >
                    <div class="summary-item">
                        <div class="summary-icon">
                            <i class="fa-solid fa-receipt"></i>
                        </div>

                        <div>
                            <span class="summary-label">
                                Movimientos
                            </span>

                            <strong class="summary-value">
                                {{ history.length }}
                            </strong>
                        </div>
                    </div>

                    <div class="summary-divider"></div>

                    <div class="summary-item">
                        <div class="summary-icon">
                            <i class="fa-solid fa-dollar-sign"></i>
                        </div>

                        <div>
                            <span class="summary-label">
                                Total aportado
                            </span>

                            <strong class="summary-value">
                                {{ totalHistoryAmount | toCurrency }}
                            </strong>
                        </div>
                    </div>
                </div>

                <!-- Ayuda -->
                <div
                    v-if="history.length > 0"
                    class="distribution-help"
                >
                    <div class="distribution-help-icon">
                        <i class="fa-solid fa-circle-info"></i>
                    </div>

                    <div>
                        <strong>Distribución de cada aportación</strong>

                        <p class="mb-0">
                            Un mismo pago puede aplicarse a una o varias aportaciones.
                            En la columna <strong>Aplicado a</strong> puedes consultar
                            exactamente cómo se distribuyó el dinero.
                        </p>
                    </div>
                </div>

                <!-- Tabla -->
                <div
                    v-if="history.length > 0"
                    class="history-table-container"
                >
                    <table class="table history-table mb-0">
                        <thead>
                            <tr>
                                <th class="col-date">
                                    Fecha
                                </th>

                                <th class="col-amount">
                                    Monto
                                </th>

                                <th class="col-distribution">
                                    Aplicado a
                                </th>

                                <th>
                                    Origen
                                </th>

                                <th>
                                    Forma de pago
                                </th>

                                <th class="col-actions">
                                    Opciones
                                </th>
                            </tr>
                        </thead>

                        <tbody>
                            <tr
                                v-for="h in history"
                                :key="h.id"
                            >
                                <!-- Fecha -->
                                <td>
                                    <div class="date-cell">
                                        <i class="fa-regular fa-calendar"></i>

                                        <span>
                                            {{ formatDate(h.date) }}
                                        </span>
                                    </div>
                                </td>

                                <!-- Monto -->
                                <td>
                                    <div class="amount-cell">
                                        {{ h.amount | toCurrency }}
                                    </div>
                                </td>

                                <!-- Distribución -->
                                <td>
                                    <div
                                        v-if="h.details && h.details.length > 0"
                                        class="distribution-list"
                                    >
                                        <div
                                            v-for="detail in h.details"
                                            :key="
                                                detail.id ||
                                                `${h.id}-${detail.program_installment_id}`
                                            "
                                            class="distribution-item"
                                        >
                                            <div class="installment-badge">
                                                A-{{ detail.aportacion_number }}
                                            </div>

                                            <div class="distribution-amount">
                                                {{ detail.amount | toCurrency }}
                                            </div>
                                        </div>

                                        <!-- Validación visual del total -->
                                        <div
                                            v-if="hasDistributionDifference(h)"
                                            class="distribution-warning"
                                        >
                                            <i class="fa-solid fa-triangle-exclamation"></i>
                                            La distribución no coincide con el monto.
                                        </div>
                                    </div>

                                    <div
                                        v-else
                                        class="distribution-empty"
                                    >
                                        <i class="fa-solid fa-minus"></i>
                                        Sin detalle de distribución
                                    </div>
                                </td>

                                <!-- Origen -->
                                <td>
                                    <span class="origin-label">
                                        {{ h.source_name || 'N/A' }}
                                    </span>
                                </td>

                                <!-- Forma de pago -->
                                <td>
                                    <div class="payment-method">
                                        <i class="fa-regular fa-credit-card"></i>

                                        <span>
                                            {{ h.type_payments_id || 'N/A' }}
                                        </span>
                                    </div>
                                </td>

                                <!-- Acciones -->
                                <td>
                                    <div class="actions-container">

                                        <a
                                            class="btn btn-sm btn-outline-info m-1"
                                            target="_blank"
                                            title="Ver recibo"
                                            :href="`${globalUrlFiles}/contributions?program_id=${h.id}`"
                                        >
                                            <i class="fa-solid fa-file-lines mr-1"></i> 
                                            <span>Recibo</span>
                                        </a>

                                        <button
                                            type="button"
                                            class="btn btn-sm btn-outline-primary m-1"
                                            title="Enviar recibo por correo"
                                            @click="sendVoucher(h.id)"
                                        >
                                            <i class="fa-solid fa-envelope mr-1"></i> 
                                            <span>Enviar</span>
                                        </button>

                                        <button
                                            v-can="'delete_programs_investors'"
                                            type="button"
                                            class="btn btn-sm btn-outline-danger m-1"
                                            title="Eliminar aportación"
                                            @click="confirmDelete(h.id)"
                                        >
                                            <i class="fa-solid fa-trash mr-1"></i> 
                                            <span>Eliminar</span>
                                        </button>
                                    </div>

                                    <div
                                        v-if="h.send == 1"
                                        class="sent-status"
                                    >
                                        <i class="fa-regular fa-circle-check"></i>
                                        Recibo enviado
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Estado vacío -->
                <div
                    v-else
                    class="empty-state"
                >
                    <div class="empty-state-icon">
                        <i class="fa-solid fa-folder-open"></i>
                    </div>

                    <h5>
                        Sin aportaciones registradas
                    </h5>

                    <p>
                        Este inversionista todavía no tiene movimientos
                        registrados en este programa.
                    </p>
                </div>

            </template>

            <!-- Footer -->
            <div class="modal-footer contribution-footer">
                <button
                    type="button"
                    class="btn btn-outline-secondary"
                    @click="closeModal"
                >
                    Cerrar
                </button>
            </div>

        </div>
    </modal>
</template>

<script>
import request from "@/services/request";
import { mapActions } from "vuex";

export default {
    data() {
        return {
            loading: true,
            program_group_id: "",
            user_id: "",
            user_name: "",
            company_id: "",
            history: [],
            user_email: ""
        };
    },

    computed: {
        totalHistoryAmount() {
            return this.history.reduce((total, item) => {
                return total + Number(item.amount || 0);
            }, 0);
        }
    },

    methods: {
        async loadHistory() {
            this.loading = true;

            try {
                let url = `/program-group-contributions/${this.program_group_id}/user/${this.user_id}/${this.company_id}`;

                let { data } = await request.get(url);

                this.history = data;
            } catch (error) {
                console.log(error);
            }

            this.loading = false;
        },

        /**
         * Convierte YYYY-MM-DD a DD/MM/YYYY
         */
        formatDate(date) {
            if (!date) {
                return "N/A";
            }

            const parts = date.split("-");

            if (parts.length !== 3) {
                return date;
            }

            return `${parts[2]}/${parts[1]}/${parts[0]}`;
        },

        /**
         * Obtiene cuánto dinero fue distribuido
         * entre las aportaciones del movimiento.
         */
        distributedAmount(contribution) {
            if (
                !contribution.details ||
                contribution.details.length === 0
            ) {
                return 0;
            }

            return contribution.details.reduce((total, detail) => {
                return total + Number(detail.amount || 0);
            }, 0);
        },

        /**
         * Ayuda a detectar visualmente si existe
         * una diferencia entre el monto registrado
         * y lo distribuido.
         */
        hasDistributionDifference(contribution) {
            if (
                !contribution.details ||
                contribution.details.length === 0
            ) {
                return false;
            }

            const amount = Number(contribution.amount || 0);
            const distributed = this.distributedAmount(contribution);

            return Math.abs(amount - distributed) > 0.01;
        },

        sendVoucher(contributionId) {
            let email = this.user_email;

            Event.$emit(
                "event-send-voucher-contribution",
                contributionId,
                email
            );
        },

        closeModal() {
            this.history = [];
            this.loading = false;

            this.$modal.hide(
                "modalProgramGroupContributionHistory"
            );
        },

        confirmDelete(id) {
            this.$swal({
                title: "¿Eliminar aportación?",
                text: "Esta acción no borra el historial, solo marcará el registro como eliminado.",
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Eliminar",
                cancelButtonText: "Cancelar",
            }).then((result) => {
                if (result.isConfirmed) {
                    this.deleteContribution(id);
                }
            });
        },

        async deleteContribution(id) {
            try {
                let { data } = await request.destroy(
                    `/program-group-contributions/${id}`
                );

                this.successNotification(data.message);

                await this.loadHistory();

                Event.$emit(
                    "event-reload-program-relations"
                );

            } catch (error) {
                this.errorNotification(error);
            }
        },

        ...mapActions({
            errorNotification: "alert/error",
            successNotification: "alert/success",
        }),
    },

    created() {
        Event.$on(
            "event-open-program-group-contribution-history",
            async (payload) => {

                this.program_group_id =
                    payload.program_group_id;

                this.user_id =
                    payload.user_id;

                this.company_id =
                    payload.company_id;

                this.user_name =
                    payload.user_name || "";

                this.user_email =
                    payload.user_email || "";

                this.$modal.show(
                    "modalProgramGroupContributionHistory"
                );

                await this.loadHistory();
            }
        );

        Event.$on(
            "event-reload-program-relations-current",
            async () => {
                await this.loadHistory();
            }
        );
    },
};
</script>

<style scoped>

/* =========================================================
   MODAL
========================================================= */

.contribution-modal-header {
    background: #0b1d34;
    color: #ffffff;
    border-bottom: 0;
    padding: 18px 22px;
}

.contribution-modal-header .modal-title {
    font-size: 18px;
    font-weight: 700;
    margin: 0;
}

.investor-name {
    margin-top: 5px;
    font-size: 13px;
    color: #d5e1f0;
}

.contribution-modal-body {
    padding: 20px 22px 0;
    background: #f8fafc;
}

/* =========================================================
   LOADING
========================================================= */

.loading-container {
    min-height: 250px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    color: #64748b;
}

/* =========================================================
   RESUMEN
========================================================= */

.history-summary {
    display: flex;
    align-items: center;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 14px 18px;
    margin-bottom: 14px;
}

.summary-item {
    display: flex;
    align-items: center;
    gap: 10px;
}

.summary-icon {
    width: 38px;
    height: 38px;
    border-radius: 8px;
    background: #eef6fb;
    color: #0b1d34;
    display: flex;
    align-items: center;
    justify-content: center;
}

.summary-label {
    display: block;
    color: #64748b;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.4px;
}

.summary-value {
    display: block;
    color: #172033;
    font-size: 15px;
    margin-top: 1px;
}

.summary-divider {
    width: 1px;
    height: 35px;
    background: #e2e8f0;
    margin: 0 25px;
}

/* =========================================================
   INFORMACIÓN
========================================================= */

.distribution-help {
    display: flex;
    gap: 12px;
    align-items: flex-start;
    background: #eef8fb;
    border: 1px solid #b9e5ef;
    border-left: 4px solid #00b2d9;
    border-radius: 6px;
    padding: 12px 14px;
    margin-bottom: 15px;
    color: #334155;
    font-size: 12px;
}

.distribution-help-icon {
    color: #00a0c4;
    font-size: 17px;
    padding-top: 1px;
}

/* =========================================================
   TABLA
========================================================= */

.history-table-container {
    background: #ffffff;
    border: 1px solid #d8e0e8;
    border-radius: 7px;
    overflow-x: auto;
}

.history-table {
    width: 100%;
    min-width: 1100px;
    margin: 0;
}

.history-table thead th {
    background: #f1f5f9;
    border-top: 0;
    border-bottom: 1px solid #cbd5e1;
    color: #334155;
    font-size: 12px;
    font-weight: 700;
    padding: 12px 10px;
    vertical-align: middle;
    white-space: nowrap;
}

.history-table tbody td {
    padding: 13px 10px;
    border-color: #e2e8f0;
    vertical-align: middle;
    font-size: 13px;
    color: #334155;
}

.history-table tbody tr:hover {
    background: #fafcfe;
}

.col-date {
    width: 130px;
}

.col-amount {
    width: 150px;
}

.col-distribution {
    min-width: 220px;
}

.col-actions {
    min-width: 250px;
}

/* =========================================================
   FECHA
========================================================= */

.date-cell {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 7px;
    white-space: nowrap;
    color: #475569;
}

.date-cell i {
    color: #94a3b8;
}

/* =========================================================
   MONTO
========================================================= */

.amount-cell {
    font-size: 14px;
    font-weight: 700;
    color: #0f172a;
    white-space: nowrap;
}

/* =========================================================
   DISTRIBUCIÓN
========================================================= */

.distribution-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
    max-width: 260px;
    margin: 0 auto;
}

.distribution-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 6px 8px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
}

.installment-badge {
    min-width: 40px;
    padding: 3px 8px;
    background: #0b1d34;
    color: #ffffff;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 700;
    text-align: center;
}

.distribution-amount {
    font-size: 12px;
    font-weight: 600;
    color: #334155;
    white-space: nowrap;
}

.distribution-empty {
    color: #94a3b8;
    font-size: 11px;
}

.distribution-warning {
    margin-top: 3px;
    color: #dc2626;
    font-size: 10px;
}

/* =========================================================
   ORIGEN / FORMA DE PAGO
========================================================= */

.origin-label {
    color: #475569;
    font-size: 12px;
}

.payment-method {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #475569;
    white-space: nowrap;
}

.payment-method i {
    color: #64748b;
}



/* =========================================================
   EMPTY
========================================================= */

.empty-state {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    min-height: 270px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #64748b;
}

.empty-state-icon {
    width: 55px;
    height: 55px;
    border-radius: 50%;
    background: #f1f5f9;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 22px;
    margin-bottom: 12px;
}

.empty-state h5 {
    color: #334155;
    margin-bottom: 5px;
}

.empty-state p {
    font-size: 12px;
    margin: 0;
}

/* =========================================================
   FOOTER
========================================================= */

.contribution-footer {
    margin: 18px -22px 0;
    padding: 13px 22px;
    background: #ffffff;
    border-top: 1px solid #e2e8f0;
    justify-content: flex-end;
}

.notification-badge {
    border-radius: 30px;
    padding: 7px;
}

/* =========================================================
   RESPONSIVE
========================================================= */

@media (max-width: 992px) {

    .history-summary {
        align-items: flex-start;
        flex-direction: column;
        gap: 12px;
    }

    .summary-divider {
        width: 100%;
        height: 1px;
        margin: 0;
    }
}

</style>